import { useEffect, useMemo } from "react";
import { throttle } from "lodash";
import { CANVAS_SIZE_ID } from "../../Canvas/constants";
import { useBuilderStore } from "../../Hooks/useBuilderStore";

export const useNonIframeResizeListeners = ({
  updatePosition,
}: {
  updatePosition: () => void;
}) => {
  const { selectedBlock } = useBuilderStore(state => {
    const value = state.getSelectedBlock();
    return {
      selectedBlockPath: state.selectedBlockPath,
      selectedBlock: value?.block,
    };
  });

  const resizeObserver = useMemo(
    () =>
      new ResizeObserver(entries => {
        for (const entry of entries) {
          if (entry.contentRect) {
            updatePosition();
          }
        }
      }),
    [updatePosition],
  );

  useEffect(() => {
    const throttledUpdatePosition = throttle(updatePosition, 100);
    document.addEventListener("scroll", throttledUpdatePosition);
    window.addEventListener("resize", throttledUpdatePosition);
    window.addEventListener("scroll", throttledUpdatePosition);

    const canvasSizeElement = document.getElementById(CANVAS_SIZE_ID);
    if (canvasSizeElement) {
      resizeObserver.observe(canvasSizeElement);
    }

    return () => {
      throttledUpdatePosition.cancel();
      document.removeEventListener("scroll", throttledUpdatePosition);
      window.removeEventListener("resize", throttledUpdatePosition);
      window.removeEventListener("scroll", throttledUpdatePosition);
      if (canvasSizeElement) resizeObserver.unobserve(canvasSizeElement);
    };
  }, [selectedBlock, updatePosition, resizeObserver]);

  useEffect(() => {
    const { id } = selectedBlock || {};
    if (!id) {
      return;
    }

    const blockElement = document.getElementById(id);
    if (!blockElement) {
      return;
    }

    resizeObserver.observe(blockElement);

    const images = blockElement.querySelectorAll("img");
    images.forEach(img => {
      if (img.complete) {
        updatePosition();
      } else {
        img.addEventListener("load", updatePosition);
      }
    });

    return () => {
      resizeObserver.unobserve(blockElement);
      images.forEach(img => {
        img.removeEventListener("load", updatePosition);
      });
    };
  }, [selectedBlock, resizeObserver, updatePosition]);
};
